"use client"

import { useState, useMemo } from "react"
import { RotateCcw, AlertTriangle } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

const T = {
  es: {
    datosEmpleado: "Datos del empleado",
    limpiar: "Limpiar",
    brutoMensual: "Salario bruto mensual (€)",
    horasContrato: "Horas semanales de contrato",
    horasExtra: "Horas extra este mes",
    tipoHora: "Tipo de hora extra",
    normal: "Normal (+25%)",
    nocturna: "Nocturna (+50%)",
    festiva: "Festiva (+75%)",
    recargosTitulo: "Recargos de referencia:",
    recargos: " Normal +25%, Nocturna +50%, Festiva +75%. La ley no fija recargos: los pone tu convenio, así que ajústalos al tuyo.",
    resultados: "Resultados",
    limiteTitulo: "⚠️ Límite legal superado",
    limite: (h: number, anual: number) =>
      `Con ${h}h/mes, acumularías ~${anual}h/año. El máximo legal son 80 horas extra al año.`,
    horaNormal: "Hora normal",
    horaExtra: (pct: number) => `Hora extra (+${pct}%)`,
    totalExtra: (h: number) => `Total horas extra (${h}h)`,
    sobreBase: (pct: string) => `+${pct}% sobre el salario base`,
    nuevoSalario: "Nuevo salario mensual",
    base: "Base",
    normativa: "📋 Normativa",
    norma1: "• Máximo legal: 80 horas extra/año",
    norma2: "• Se pagan al menos como la hora ordinaria; el recargo lo fija el convenio",
    norma3: "• Compensación: retribución o descanso equivalente",
    norma4: "• Horas complementarias (parciales): se pagan como ordinarias",
  },
  en: {
    datosEmpleado: "Employee details",
    limpiar: "Reset",
    brutoMensual: "Gross monthly salary (€)",
    horasContrato: "Contracted hours per week",
    horasExtra: "Overtime hours this month",
    tipoHora: "Type of overtime",
    normal: "Standard (+25%)",
    nocturna: "Night (+50%)",
    festiva: "Public holiday (+75%)",
    recargosTitulo: "Reference premiums used:",
    recargos: " standard +25%, night +50%, public holiday +75%. Spanish law sets no premium: your collective agreement (convenio) does, so adjust these to yours.",
    resultados: "Results",
    limiteTitulo: "⚠️ Legal limit exceeded",
    limite: (h: number, anual: number) =>
      `At ${h}h a month, this adds up to ~${anual}h a year. The Spanish Workers' Statute caps overtime at 80 hours a year.`,
    horaNormal: "Standard hour",
    horaExtra: (pct: number) => `Overtime hour (+${pct}%)`,
    totalExtra: (h: number) => `Total overtime (${h}h)`,
    sobreBase: (pct: string) => `+${pct}% on top of base salary`,
    nuevoSalario: "New monthly salary",
    base: "Base",
    normativa: "📋 Spanish rules",
    norma1: "• Legal cap: 80 overtime hours a year (Workers' Statute)",
    norma2: "• Overtime is paid at no less than the ordinary hourly rate; the premium is set by the collective agreement",
    norma3: "• Compensation: extra pay or equivalent time off in lieu",
    norma4: "• Additional hours for part-time staff (horas complementarias): paid at the ordinary rate",
  },
}

const defaultState = {
  brutoMensual: 1500,
  horasContrato: 40,
  horasExtra: 10,
  tipoHora: "normal" as "normal" | "nocturna" | "festiva",
}

export default function HorasExtraCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const [brutoMensual, setBrutoMensual] = useState(defaultState.brutoMensual)
  const [horasContrato, setHorasContrato] = useState(defaultState.horasContrato)
  const [horasExtra, setHorasExtra] = useState(defaultState.horasExtra)
  const [tipoHora, setTipoHora] = useState(defaultState.tipoHora)

  const results = useMemo(() => {
    if (brutoMensual <= 0 || horasContrato <= 0) {
      return {
        costeHoraNormal: 0,
        recargoPct: 0,
        costeHoraExtra: 0,
        totalHorasExtra: 0,
        nuevoSalarioMensual: 0,
        incrementoPct: 0,
        horasAnualesEstimadas: 0,
        superaMaximo: false,
      }
    }

    // Coste hora normal: bruto mensual / (horas semanales * 4.33 semanas/mes)
    const horasMensuales = horasContrato * 4.33
    const costeHoraNormal = brutoMensual / horasMensuales

    // Recargos según tipo
    const recargos: Record<typeof tipoHora, number> = {
      normal: 25,
      nocturna: 50,
      festiva: 75,
    }
    const recargoPct = recargos[tipoHora]
    const costeHoraExtra = costeHoraNormal * (1 + recargoPct / 100)

    // Total
    const totalHorasExtra = costeHoraExtra * horasExtra
    const nuevoSalarioMensual = brutoMensual + totalHorasExtra
    const incrementoPct = (totalHorasExtra / brutoMensual) * 100

    // Horas anuales estimadas (si se repite cada mes)
    const horasAnualesEstimadas = horasExtra * 12
    const superaMaximo = horasAnualesEstimadas > 80

    return {
      costeHoraNormal,
      recargoPct,
      costeHoraExtra,
      totalHorasExtra,
      nuevoSalarioMensual,
      incrementoPct,
      horasAnualesEstimadas,
      superaMaximo,
    }
  }, [brutoMensual, horasContrato, horasExtra, tipoHora])

  const reset = () => {
    setBrutoMensual(defaultState.brutoMensual)
    setHorasContrato(defaultState.horasContrato)
    setHorasExtra(defaultState.horasExtra)
    setTipoHora(defaultState.tipoHora)
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosEmpleado}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.brutoMensual}
            </label>
            <input
              type="number"
              value={brutoMensual || ""}
              onChange={(e) => setBrutoMensual(parseFloat(e.target.value) || 0)}
              placeholder="1500"
              min="0"
              step="50"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.horasContrato}
            </label>
            <input
              type="number"
              value={horasContrato || ""}
              onChange={(e) => setHorasContrato(parseFloat(e.target.value) || 0)}
              placeholder="40"
              min="1"
              max="40"
              step="1"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.horasExtra}
            </label>
            <input
              type="number"
              value={horasExtra || ""}
              onChange={(e) => setHorasExtra(parseFloat(e.target.value) || 0)}
              placeholder="10"
              min="0"
              step="1"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.tipoHora}
            </label>
            <select
              value={tipoHora}
              onChange={(e) => setTipoHora(e.target.value as typeof tipoHora)}
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            >
              <option value="normal">{t.normal}</option>
              <option value="nocturna">{t.nocturna}</option>
              <option value="festiva">{t.festiva}</option>
            </select>
          </div>

          {/* Legal info */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
            <p className="text-sm text-sky-800">
              <strong>{t.recargosTitulo}</strong>{t.recargos}
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Warning if over 80h */}
          {results.superaMaximo && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">{t.limiteTitulo}</p>
                <p className="text-sm text-red-700 mt-1">
                  {t.limite(horasExtra, results.horasAnualesEstimadas)}
                </p>
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.horaNormal}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {eur(results.costeHoraNormal)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
              <p className="text-xs text-amber-100 mb-1">{t.horaExtra(results.recargoPct)}</p>
              <p className="text-2xl font-bold">{eur(results.costeHoraExtra)}</p>
            </div>
          </div>

          {/* Total extra */}
          <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-6 text-white">
            <p className="text-sm font-medium text-violet-100 mb-1">
              {t.totalExtra(horasExtra)}
            </p>
            <p className="text-4xl font-bold">{eur(results.totalHorasExtra)}</p>
            <p className="text-sm text-violet-200 mt-1">
              {t.sobreBase(idioma === "en" ? numeroEn(results.incrementoPct, 1) : results.incrementoPct.toFixed(1))}
            </p>
          </div>

          {/* New salary */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-zinc-500 mb-1">{t.nuevoSalario}</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {eur(results.nuevoSalarioMensual)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 mb-1">{t.base}</p>
                <p className="text-lg text-zinc-600">{eur(brutoMensual)}</p>
              </div>
            </div>
          </div>

          {/* Benchmark */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.normativa}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              <li>{t.norma1}</li>
              <li>{t.norma2}</li>
              <li>{t.norma3}</li>
              <li>{t.norma4}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
